<template>
    <layout>
        <HeaderAdminComponent title="Taxonomía"></HeaderAdminComponent>

        <main class="container px-4 py-8 mx-auto">
            <section class="p-5 border shadow-sm md:p-7 rounded-3xl border-light_gray bg-light_yellow">
                <p class="text-sm font-bold tracking-wider uppercase text-light_green">Catálogo compartido</p>
                <h1 class="mt-2 text-2xl font-bold text-enriqueto_green">Árbol de la vida</h1>
                <p class="max-w-4xl mt-2 text-sm leading-relaxed text-gray">
                    Cada término se crea una sola vez. El campo “Término superior” construye la rama y luego cada producto se vincula con sus especies o con el último nivel que tengas disponible.
                </p>
            </section>

            <div class="grid gap-6 mt-6 lg:grid-cols-[minmax(19rem,0.75fr)_minmax(0,1.25fr)]">
                <section class="p-5 bg-white border shadow-sm rounded-3xl border-light_gray">
                    <div class="flex items-center justify-between gap-3">
                        <h2 class="text-xl font-bold text-enriqueto_green">{{ form.id ? 'Editar término' : 'Nuevo término' }}</h2>
                        <button v-if="form.id" type="button" class="text-sm font-bold text-enriqueto_green" @click="resetForm">Cancelar</button>
                    </div>

                    <form class="grid gap-4 mt-5" @submit.prevent="saveTerm">
                        <label class="grid gap-1 text-sm font-bold text-enriqueto_green">
                            Nombre científico o taxonómico
                            <input v-model="form.name" required class="px-4 py-3 font-normal text-gray border rounded-xl border-light_gray" placeholder="Ej. Coffea arabica">
                        </label>
                        <div class="grid gap-4 md:grid-cols-2">
                            <label class="grid gap-1 text-sm font-bold text-enriqueto_green">
                                Rango
                                <input v-model="form.rank" required class="px-4 py-3 font-normal uppercase text-gray border rounded-xl border-light_gray" placeholder="ESPECIE">
                            </label>
                            <label class="grid gap-1 text-sm font-bold text-enriqueto_green">
                                Orden visual
                                <input v-model.number="form.sort_order" type="number" min="0" class="px-4 py-3 font-normal text-gray border rounded-xl border-light_gray">
                            </label>
                        </div>
                        <label class="grid gap-1 text-sm font-bold text-enriqueto_green">
                            Término superior
                            <select v-model="form.parent_id" class="px-4 py-3 font-normal text-gray bg-white border rounded-xl border-light_gray">
                                <option :value="null">Ninguno: raíz del árbol</option>
                                <option v-for="term in parentOptions" :key="term.id" :value="term.id">
                                    {{ term.rank }} — {{ term.name }}
                                </option>
                            </select>
                        </label>
                        <label class="grid gap-1 text-sm font-bold text-enriqueto_green">
                            Slug de la página
                            <input v-model="form.slug" class="px-4 py-3 font-normal text-gray border rounded-xl border-light_gray" placeholder="Se genera automáticamente">
                        </label>
                        <label class="grid gap-1 text-sm font-bold text-enriqueto_green">
                            Descripción
                            <textarea v-model="form.description" rows="5" class="px-4 py-3 font-normal leading-relaxed text-gray border resize-y rounded-xl border-light_gray" placeholder="Definición que aparecerá en la página del término"></textarea>
                        </label>
                        <label class="grid gap-1 text-sm font-bold text-enriqueto_green">
                            Nombres alternativos
                            <input v-model="form.aliases" class="px-4 py-3 font-normal text-gray border rounded-xl border-light_gray" placeholder="Separados por comas">
                        </label>
                        <label class="grid gap-1 text-sm font-bold text-enriqueto_green">
                            Fuente
                            <input v-model="form.source_url" type="url" class="px-4 py-3 font-normal text-gray border rounded-xl border-light_gray" placeholder="https://...">
                        </label>

                        <p v-if="errorMessage" class="p-3 text-sm font-bold text-red bg-red bg-opacity-10 rounded-xl">{{ errorMessage }}</p>
                        <p v-if="successMessage" class="p-3 text-sm font-bold rounded-xl text-enriqueto_green bg-light_yellow">{{ successMessage }}</p>

                        <button type="submit" :disabled="saving" class="px-5 py-3 font-bold text-white rounded-xl bg-enriqueto_green disabled:opacity-60">
                            {{ saving ? 'Guardando…' : (form.id ? 'Guardar cambios' : 'Crear término') }}
                        </button>
                    </form>
                </section>

                <section class="p-5 bg-white border shadow-sm rounded-3xl border-light_gray">
                    <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h2 class="text-xl font-bold text-enriqueto_green">Términos registrados</h2>
                            <p class="mt-1 text-sm text-gray">{{ terms.length }} términos disponibles para todos los productos.</p>
                        </div>
                        <input v-model="search" class="px-4 py-2 border rounded-xl border-light_gray" placeholder="Buscar término">
                    </div>

                    <div class="grid gap-3 mt-5">
                        <article v-for="term in filteredTerms" :key="term.id" class="flex flex-col gap-3 p-4 border md:flex-row md:items-start md:justify-between rounded-2xl border-light_gray">
                            <div class="min-w-0">
                                <div class="flex flex-wrap items-center gap-2">
                                    <span class="text-xs font-bold tracking-wider uppercase text-light_green">{{ term.rank }}</span>
                                    <span class="text-xs text-gray">Orden {{ term.sort_order }}</span>
                                </div>
                                <h3 class="mt-1 text-lg font-bold break-words text-enriqueto_green">{{ term.name }}</h3>
                                <p class="mt-1 text-xs text-gray">Depende de: {{ term.parent ? term.parent.name : 'raíz del árbol' }}</p>
                                <p v-if="term.description" class="mt-2 text-sm leading-relaxed text-gray line-clamp-2">{{ term.description }}</p>
                            </div>
                            <button type="button" class="self-start px-4 py-2 text-sm font-bold border rounded-xl border-enriqueto_green text-enriqueto_green" @click="editTerm(term)">
                                Editar
                            </button>
                        </article>
                    </div>
                </section>
            </div>
        </main>
    </layout>
</template>

<script setup>
import axios from 'axios'
import { computed, reactive, ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import Layout from '../../Components/Shared/LayoutAdmin.vue'
import HeaderAdminComponent from '../../Components/Shared/HeaderAdminComponent.vue'

const props = defineProps({ terms: { type: Array, default: () => [] } })

const blankForm = () => ({
    id: null,
    name: '',
    slug: '',
    rank: '',
    parent_id: null,
    description: '',
    aliases: '',
    source_url: '',
    sort_order: 0,
})

const form = reactive(blankForm())
const search = ref('')
const saving = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const filteredTerms = computed(() => {
    const needle = search.value.trim().toLocaleLowerCase('es')
    if (!needle) return props.terms
    return props.terms.filter(term => `${term.rank} ${term.name} ${term.description || ''}`.toLocaleLowerCase('es').includes(needle))
})

const parentOptions = computed(() => props.terms.filter(term => term.id !== form.id))

function resetForm() {
    Object.assign(form, blankForm())
    errorMessage.value = ''
    successMessage.value = ''
}

function editTerm(term) {
    Object.assign(form, {
        id: term.id,
        name: term.name,
        slug: term.slug,
        rank: term.rank,
        parent_id: term.parent_id,
        description: term.description || '',
        aliases: Array.isArray(term.aliases) ? term.aliases.join(', ') : '',
        source_url: term.source_url || '',
        sort_order: term.sort_order || 0,
    })
    errorMessage.value = ''
    successMessage.value = ''
    window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function saveTerm() {
    saving.value = true
    errorMessage.value = ''
    successMessage.value = ''

    try {
        const wasEditing = Boolean(form.id)
        if (form.id) {
            await axios.put(`/resource/taxonomy-term/${form.id}`, form)
        } else {
            await axios.post('/resource/taxonomy-term', form)
        }
        if (!wasEditing) Object.assign(form, blankForm())
        successMessage.value = wasEditing ? 'Término actualizado.' : 'Término creado.'
        Inertia.reload({ preserveScroll: true })
    } catch (error) {
        const errors = error.response?.data?.errors || {}
        errorMessage.value = Object.values(errors).flat()[0] || 'No se pudo guardar el término.'
    } finally {
        saving.value = false
    }
}
</script>
